import React, { useEffect, useState } from "react";
import Select, { SingleValue } from "react-select";
import API_BASE_URL from "../../config/apiConfig";
import { useAuth } from "../../context/AuthProvider";
import WebmasterSidebar from "../../components/WebmasterSidebar";
import StatusDropdown, { Status } from "../../components/StatusDropdown";

interface PatientOption {
  value: string;
  label: string;
}

interface Appointment {
  id: string;
  therapist_id: string;
  therapist_name: string;
  patient_id: string;
  patient_name: string;
  type: string; // "therapy" | "activity" | "medical_activity" | "medical_consult"
  appointment_description: string;
  start_time: string;
  end_time: string;
  status: string;
}

interface TimeInterval {
  start: string;
  end: string;
}

interface KeyValue {
  key: string;
  value: string;
}

const weekdays = ["Luni", "Marți", "Miercuri", "Joi", "Vineri"];

const WebmasterPatientAppointments: React.FC = () => {
  const { token } = useAuth();
  const [patients, setPatients] = useState<PatientOption[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<PatientOption | null>(null);
  const [weekStart, setWeekStart] = useState<string>(() => getCurrentMonday());
  const [timeIntervals, setTimeIntervals] = useState<TimeInterval[]>([]);

  // tipuri publice ACTIVE
  const [therapyTypes, setTherapyTypes] = useState<KeyValue[]>([]);
  const [activities, setActivities] = useState<KeyValue[]>([]);
  const [medicalActivities, setMedicalActivities] = useState<KeyValue[]>([]);
  const [consultTypes, setConsultTypes] = useState<KeyValue[]>([]);
  const [statuses, setStatuses] = useState<Status[]>([]);

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [refreshFlag, setRefreshFlag] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });

  function getCurrentMonday(): string {
    const today = new Date();
    const day = today.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    const yyyy = monday.getFullYear();
    const mm = String(monday.getMonth() + 1).padStart(2, "0");
    const dd = String(monday.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  // 1) Încarcă lista de pacienți
  useEffect(() => {
    const fetchPatients = async () => {
      const res = await fetch(`${API_BASE_URL}/patient/get-all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const opts = data.map((p: any) => ({
        value: p.id,
        label: `${p.firstname} ${p.lastname}`,
      }));
      setPatients(opts);
      if (opts.length) setSelectedPatient(opts[0]);
    };
    if (token) fetchPatients();
  }, [token]);

  // 2) Încarcă time-intervals, therapy-types, activities, medical-activities, consult-types și status-uri
useEffect(() => {
  const loadMeta = async () => {
    const [ti, th, st, act, medAct, cons] = await Promise.all([
      // trimitem weekStart ca parametru, ca backendul să returneze intervalul corect
      fetch(`${API_BASE_URL}/types/time-intervals?date=${weekStart}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      }).then(r => r.json()),
      fetch(`${API_BASE_URL}/types/therapy-type`,        { headers:{Authorization:`Bearer ${token}`} }).then(r=>r.json()),
      fetch(`${API_BASE_URL}/types/appointment-status`,  { headers:{Authorization:`Bearer ${token}`} }).then(r=>r.json()),
      fetch(`${API_BASE_URL}/types/activities`,          { headers:{Authorization:`Bearer ${token}`} }).then(r=>r.json()),
      fetch(`${API_BASE_URL}/types/medical-activities`,  { headers:{Authorization:`Bearer ${token}`} }).then(r=>r.json()).catch(()=>({data:[]})),
      fetch(`${API_BASE_URL}/types/consult-types`,       { headers:{Authorization:`Bearer ${token}`} }).then(r=>r.json()).catch(()=>({data:[]})),
    ]);
    setTimeIntervals(ti.data || []);
    setTherapyTypes(th.data || []);
    setStatuses(st.data || []);
    setActivities(act.data || []);
    setMedicalActivities(medAct.data || []);
    setConsultTypes(cons.data || []);
  };

  if (token && weekStart) loadMeta();
}, [token, weekStart]);

  // 3) De fiecare dată când se schimbă pacientul sau săptămâna, încarcă programările
  useEffect(() => {
    if (!selectedPatient) return;
    const fetchApps = async () => {
      const res = await fetch(`${API_BASE_URL}/appointments/webmaster-patients-weekly`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          week_start: weekStart,
          patient_id: selectedPatient.value,
        }),
      });
      const data = await res.json();
      setAppointments(data.data || []);
    };
    fetchApps();
  }, [selectedPatient, weekStart, token, refreshFlag]);

  const getDayDate = (offset: number): string => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + offset);
    return date.toISOString().split("T")[0];
  };

  // helpers pentru label-uri
  const getTherapyValue = (key: string) => {
    const t = therapyTypes.find((t) => t.key === key);
    return t ? t.value : key;
  };

  const getActivityValue = (key: string) => {
    const a = activities.find((x) => x.key === key);
    return a ? a.value : key;
  };

  const getMedicalActivityValue = (key: string) => {
    const a = medicalActivities.find((x) => x.key === key);
    return a ? a.value : key;
  };

  const getConsultTypeValue = (key: string) => {
    const c = consultTypes.find((x) => x.key === key);
    return c ? c.value : key;
  };

  // descriere în celulă
  const getDescription = (a: Appointment) => {
    switch (a.type) {
      case "therapy":
        return getTherapyValue(a.appointment_description);
      case "activity":
        return getActivityValue(a.appointment_description);
      case "medical_activity":
        return getMedicalActivityValue(a.appointment_description);
      case "medical_consult":
        return getConsultTypeValue(a.appointment_description);
      default:
        return a.appointment_description;
    }
  };

  const renderCell = (interval: TimeInterval, dayOffset: number) => {
    const date = getDayDate(dayOffset);
    const cellApps = appointments.filter(
      (a) => a.start_time.startsWith(date) && a.start_time.endsWith(`T${interval.start}:00`)
    );
    if (!cellApps.length) return null;

    return (
      <div className="webmaster-appointment-cell">
        {cellApps.map((a) => {
          // statusuri: pentru activity filtrăm după activity_key; pentru rest (therapy + medical) folosim cele de "therapy"
          const filteredStatuses =
            a.type === "activity"
              ? statuses.filter(s => s.type === "activity" && s.activity_key === a.appointment_description)
              : statuses.filter(s => s.type === "therapy");

          const description = getDescription(a);

          return (
            <div key={a.id} className="appointment-box">
              <div className="appointment-value appointment-text">
                {description} cu {a.therapist_name}
              </div>

              <StatusDropdown
                statuses={filteredStatuses}
                currentStatus={a.status}
                viewMode="scroll"
                onChange={st => {
                  fetch(`${API_BASE_URL}/appointments/update-status/${a.id}`, {
                    method: "PUT",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ status: st }),
                  }).then(() => {
                    setRefreshFlag(f => f + 1);
                  });
                }}
                isAdmin={true}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const formatDateRO = (iso: string) => {
    const [y,m,d] = iso.split("-");
    return `${d}.${m}.${y}`;
  };

  const handleExport = async () => {
    if (!selectedPatient) return;
    const url =
      `${API_BASE_URL}/appointments/export-patient-weekly` +
      `?export_type=xls` +
      `&week_start=${weekStart}` +
      `&patient_id=${selectedPatient.value}`;
    try {
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        const err = await res.json();
        return alert(err.detail || "Eroare la export");
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `export_${weekStart}.xls`;

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      console.error(e);
      alert("Eroare la export: " + e);
    }
  };

  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <WebmasterSidebar onCollapseChange={c => setIsCollapsed(c)} />
      <div className="page-content">
        <h1 className="page-title">Programări pacient</h1>
        <div className="page-subtitle">Săptămâna: {formatDateRO(weekStart)} - {formatDateRO(getDayDate(4))}</div>

        <div className="table-top">
          <div style={{ marginBottom: 0 }}>
            <label className="patient-label" style={{ marginRight: 8 }}>Pacient:</label>
            <Select
              options={patients}
              value={selectedPatient}
              onChange={(opt: SingleValue<PatientOption>) => setSelectedPatient(opt)}
              isSearchable
              placeholder="Caută pacient..."
              styles={{ container: base => ({ ...base, width: 300 }) }}
            />
          </div>
          <div className="appointment-date">
            <button
              onClick={handleExport}
              className="primary-btn"
              style={{ marginLeft: "auto" }}
            >
              Exportă programări
            </button>
            <label>Data: </label>
            <input
              type="date"
              value={weekStart}
              onChange={(e) => {
                const raw = e.target.value;
                const isValidISO = /^\d{4}-\d{2}-\d{2}$/.test(raw);
                if (!isValidISO) return;

                const selected = new Date(raw);
                if (isNaN(selected.getTime())) return;

                const diff = selected.getDate() - selected.getDay() + (selected.getDay() === 0 ? -6 : 1);
                const monday = new Date(selected.setDate(diff)).toISOString().split("T")[0];
                setWeekStart(monday);
              }}
            />
          </div>
        </div>

        <div className="table-rounded-container">
          <table className="parent-appointments-table">
            <thead>
              <tr>
                <th>Ora</th>
                {weekdays.map(w => <th key={w}>{w}</th>)}
              </tr>
            </thead>
            <tbody>
              {timeIntervals.map(interval => (
                <tr key={interval.start}>
                  <td>{`${interval.start} - ${interval.end}`}</td>
                  {[0,1,2,3,4].map(i => (
                    <td key={i}>{renderCell(interval, i)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WebmasterPatientAppointments;